import { ofType, StateObservable } from "redux-observable";
import { ajax } from "rxjs/ajax";
import {catchError, exhaustMap, map, mergeMap } from "rxjs/operators";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {Identifier} from "../../models/common/Identifier";
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import { Observable, of } from 'rxjs';
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import { RouteMethods} from "../../config/RouteConfig";
import {CreateProjectLogEntryActionTypes} from "./ProjectLogActions";
import {Nullable} from "../../models/common/Nullable";
import {
    ProjectLogCategory,
    ProjectLogCategoryId,
    ProjectLogCategoryProp
} from "../../models/projects/ProjectLogCategory";
import {ProjectLogModule} from "../../models/projects/ProjectLog";


export type ProjectLogCategoryActionTypes = FetchProjectLogCategoriesActionTypes
    | CreateProjectLogEntryActionTypes
    | UpdateProjectLogCategoryActionTypes
    | DeleteProjectLogCategoryActionTypes
    | RestoreProjectLogCategoryActionTypes

/**
 * API PATHS
 */
const uri = "projects/logs/categories";
const basePath = () => `${ APP_CONFIG.baseWeb }${ uri }`;

function pathWithId( id: Identifier<number> ) {
    return basePath() + "/" + id.rawValue;
}

/**
 * return the route / path to fetch tags from api backend for async select components
 *
 * @param projectId when not set, will be set to 0 to fetch all categories
 * @see AsyncSelect
 */
export function projectLogCategorySelectRoute( projectId: Nullable< Identifier<number> > = null ) {
    return uri + "/select/" + ( projectId !== null ? projectId!!.rawValue : 0 );
}

/**
 * FETCH CATEGORIES
 */
// typescript redux action types
export interface FetchProjectLogCategoriesAction extends ViewIdentifiable, ReduxAction<typeof FETCH_PROJECT_LOG_CATEGORIES> {}
export interface FetchProjectLogCategoriesSuccessAction extends ReduxAction<typeof FETCH_PROJECT_LOG_CATEGORIES_SUCCESS> {
    payload: ApiReturnType< ProjectLogCategory[] >
}
export interface FetchProjectLogCategoriesErrorAction extends ErrorAction<typeof FETCH_PROJECT_LOG_CATEGORIES_ERROR> {}

export type FetchProjectLogCategoriesActionTypes = FetchProjectLogCategoriesAction | FetchProjectLogCategoriesSuccessAction | FetchProjectLogCategoriesErrorAction

//redux action type constants
export const FETCH_PROJECT_LOG_CATEGORIES = "FETCH_PROJECT_LOG_CATEGORIES";
export const FETCH_PROJECT_LOG_CATEGORIES_SUCCESS = "FETCH_PROJECT_LOG_CATEGORIES_SUCCESS";
export const FETCH_PROJECT_LOG_CATEGORIES_ERROR = "FETCH_PROJECT_LOG_CATEGORIES_ERROR";

//redux accessor functions
export function fetchProjectLogCategories( viewIdentifier: string ): FetchProjectLogCategoriesAction {
    return { type: FETCH_PROJECT_LOG_CATEGORIES, viewIdentifier };
}
export function fetchProjectLogCategoriesSuccess( payload: ApiReturnType< ProjectLogCategory[] > ): FetchProjectLogCategoriesSuccessAction {
    return { type: FETCH_PROJECT_LOG_CATEGORIES_SUCCESS, payload };
}
export function fetchProjectLogCategoriesError( error: any ): FetchProjectLogCategoriesErrorAction {
    return { type: FETCH_PROJECT_LOG_CATEGORIES_ERROR, error };
}

//redux-observable epic
export function fetchProjectLogCategoriesEpic(
    action$: Observable< FetchProjectLogCategoriesAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_LOG_CATEGORIES ),
        mergeMap( action => {
            const request = getUserViewSettingsApiRequest( basePath(), action.viewIdentifier, store$ );

            return ajax( APP_CONFIG.getRequestObject( request.path ) ).pipe(
                map( response => fetchProjectLogCategoriesSuccess( response.response as ApiReturnType< ProjectLogCategory[] > ) ),
                catchError( error => of(
                    fetchProjectLogCategoriesError( error  ),
                    eventNotificationError( error, store$, action.viewIdentifier, "fetchProjectLogCategories" )
                ))
            );
        })
    );
}

/**
 * CREATE CATEGORY
 */
//typescript redux action types
export interface CreateProjectLogCategoryAction extends ProjectLogCategoryProp, ReduxAction<typeof CREATE_PROJECT_LOG_CATEGORY> {}
export interface CreateProjectLogCategorySuccessAction extends ReduxAction<typeof CREATE_PROJECT_LOG_CATEGORY_SUCCESS> {}
export interface CreateProjectLogCategoryErrorAction extends ErrorAction<typeof CREATE_PROJECT_LOG_CATEGORY_ERROR> {}

export type CreateProjectLogActionTypes = CreateProjectLogCategoryErrorAction | CreateProjectLogCategorySuccessAction | CreateProjectLogCategoryAction

//redux action type constants
export const CREATE_PROJECT_LOG_CATEGORY = "CREATE_PROJECT_LOG_CATEGORY";
export const CREATE_PROJECT_LOG_CATEGORY_SUCCESS = "CREATE_PROJECT_LOG_CATEGORY_SUCCESS";
export const CREATE_PROJECT_LOG_CATEGORY_ERROR = "CREATE_PROJECT_LOG_CATEGORY_ERROR";

//redux action accessor functions
export function createProjectLogCategory( projectLogCategory: ProjectLogCategory ): CreateProjectLogCategoryAction {
    return { type: CREATE_PROJECT_LOG_CATEGORY, projectLogCategory };
}
export function createProjectLogCategorySuccess(): CreateProjectLogCategorySuccessAction {
    return { type: CREATE_PROJECT_LOG_CATEGORY_SUCCESS };
}
export function createProjectLogCategoryError( error: any): CreateProjectLogCategoryErrorAction {
    return { type: CREATE_PROJECT_LOG_CATEGORY_ERROR, error };
}

//redux observable epic
export function createProjectLogCategoryEpic(
    action$: Observable< CreateProjectLogCategoryAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_PROJECT_LOG_CATEGORY ),
        exhaustMap( action => {
            return ajax( APP_CONFIG.getRequestObject( basePath(), RouteMethods.post, action.projectLogCategory ) ).pipe(
                mergeMap( _ => of(
                    createProjectLogCategorySuccess(),
                    eventNotificationSuccess(),
                    fetchProjectLogCategories( ProjectLogModule.identifier )
                ) ),
                catchError( error => of(
                    createProjectLogCategoryError( error ),
                    eventNotificationError( error, store$, ProjectLogModule.identifier, "createProjectLogCategory" )
                ))
            );
        })
    );
}

/**
 * UPDATE CATEGORY
 */
//typescript redux action types
export interface UpdateProjectLogCategoryAction extends ProjectLogCategoryProp, ReduxAction<typeof UPDATE_PROJECT_LOG_CATEGORY> {}
export interface UpdateProjectLogCategorySuccessAction extends ReduxAction<typeof UPDATE_PROJECT_LOG_CATEGORY_SUCCESS> {}
export interface UpdateProjectLogCategoryErrorAction extends ErrorAction<typeof UPDATE_PROJECT_LOG_CATEGORY_ERROR> {}

export type UpdateProjectLogCategoryActionTypes = UpdateProjectLogCategoryAction | UpdateProjectLogCategorySuccessAction | UpdateProjectLogCategoryErrorAction

//redux action type constants
export const UPDATE_PROJECT_LOG_CATEGORY = "UPDATE_PROJECT_LOG_CATEGORY";
export const UPDATE_PROJECT_LOG_CATEGORY_SUCCESS = "UPDATE_PROJECT_LOG_CATEGORY_SUCCESS";
export const UPDATE_PROJECT_LOG_CATEGORY_ERROR = "UPDATE_PROJECT_LOG_CATEGORY_ERROR";

//redux action accessor functions
export function updateProjectLogCategory( projectLogCategory: ProjectLogCategory ): UpdateProjectLogCategoryAction {
    return { type: UPDATE_PROJECT_LOG_CATEGORY, projectLogCategory };
}
export function updateProjectLogCategorySuccess(): UpdateProjectLogCategorySuccessAction {
    return { type: UPDATE_PROJECT_LOG_CATEGORY_SUCCESS };
}
export function updateProjectLogCategoryError( error: any ): UpdateProjectLogCategoryErrorAction {
    return { type: UPDATE_PROJECT_LOG_CATEGORY_ERROR, error };
}

//redux observable epic
export function updateProjectLogCategoryEpic(
    action$: Observable< UpdateProjectLogCategoryAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_PROJECT_LOG_CATEGORY ),
        exhaustMap( action => {
            return ajax(
                APP_CONFIG.getRequestObject(
                    pathWithId( action.projectLogCategory.projectLogCategoryId ),
                    RouteMethods.put,
                    action.projectLogCategory
                )
            ).pipe(
                mergeMap( _ => of(
                    updateProjectLogCategorySuccess(),
                    eventNotificationSuccess(),
                    fetchProjectLogCategories( ProjectLogModule.identifier )
                )),
                catchError( error => of(
                    updateProjectLogCategoryError( error ),
                    eventNotificationError( error, store$, ProjectLogModule.identifier, "updateProjectLogCategory" )
                ) )
            )
        })
    );
}

/**
 * DELETE CATEGORY
 */
//typescript redux action types
export interface DeleteProjectLogCategoryAction extends ProjectLogCategoryId, ReduxAction<typeof DELETE_PROJECT_LOG_CATEGORY> {}
export interface DeleteProjectLogCategorySuccessAction extends ReduxAction<typeof DELETE_PROJECT_LOG_CATEGORY_SUCCESS> {}
export interface DeleteProjectLogCategoryErrorAction extends ErrorAction<typeof DELETE_PROJECT_LOG_CATEGORY_ERROR> {}

export type DeleteProjectLogCategoryActionTypes = DeleteProjectLogCategoryAction | DeleteProjectLogCategorySuccessAction | DeleteProjectLogCategoryErrorAction

//redux action type constants
export const DELETE_PROJECT_LOG_CATEGORY = "DELETE_PROJECT_LOG_CATEGORY";
export const DELETE_PROJECT_LOG_CATEGORY_SUCCESS = "DELETE_PROJECT_LOG_CATEGORY_SUCCESS";
export const DELETE_PROJECT_LOG_CATEGORY_ERROR = "DELETE_PROJECT_LOG_CATEGORY_ERROR";

//redux action accessor functions
export function deleteProjectLogCategory( projectLogCategoryId: Identifier<number> ): DeleteProjectLogCategoryAction {
    return { type: DELETE_PROJECT_LOG_CATEGORY, projectLogCategoryId };
}
export function deleteProjectLogCategorySuccess(): DeleteProjectLogCategorySuccessAction {
    return { type: DELETE_PROJECT_LOG_CATEGORY_SUCCESS };
}
export function deleteProjectLogCategoryError( error: any ): DeleteProjectLogCategoryErrorAction {
    return { type: DELETE_PROJECT_LOG_CATEGORY_ERROR, error };
}

//redux observable epic
export function deleteProjectLogCategoryEpic(
    action$: Observable< DeleteProjectLogCategoryAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_PROJECT_LOG_CATEGORY ),
        exhaustMap( action => ajax(
            APP_CONFIG.getRequestObject( pathWithId( action.projectLogCategoryId ), RouteMethods.delete )
        ).pipe(
            mergeMap( _ => of(
                deleteProjectLogCategorySuccess(),
                eventNotificationSuccess(),
                fetchProjectLogCategories( ProjectLogModule.identifier )
            ) ),
            catchError( error => of(
                deleteProjectLogCategoryError( error ),
                eventNotificationError( error, store$, ProjectLogModule.identifier, "deleteProjectLogCategory" )
            ))
        ))
    );
}

/**
 * RESTORE CATEGORY
 */
//typescript redux action types
export interface RestoreProjectLogCategoryAction extends ProjectLogCategoryId, ReduxAction< typeof RESTORE_PROJECT_LOG_CATEGORY> {}
export interface RestoreProjectLogCategorySuccessAction extends ReduxAction< typeof RESTORE_PROJECT_LOG_CATEGORY_SUCCESS> {}
export interface RestoreProjectLogCategoryErrorAction extends ErrorAction< typeof RESTORE_PROJECT_LOG_CATEGORY_ERROR> {}

export type RestoreProjectLogCategoryActionTypes = RestoreProjectLogCategoryAction | RestoreProjectLogCategorySuccessAction | RestoreProjectLogCategoryErrorAction

//redux action type constants
export const RESTORE_PROJECT_LOG_CATEGORY = "RESTORE_PROJECT_LOG_CATEGORY";
export const RESTORE_PROJECT_LOG_CATEGORY_SUCCESS = "RESTORE_PROJECT_LOG_CATEGORY_SUCCESS";
export const RESTORE_PROJECT_LOG_CATEGORY_ERROR = "RESTORE_PROJECT_LOG_CATEGORY_ERROR";

//redux action accessor functions
export function restoreProjectLogCategory( projectLogCategoryId: Identifier<number> ): RestoreProjectLogCategoryAction {
    return { type: RESTORE_PROJECT_LOG_CATEGORY, projectLogCategoryId };
}
export function restoreProjectLogCategorySuccess(): RestoreProjectLogCategorySuccessAction {
    return { type: RESTORE_PROJECT_LOG_CATEGORY_SUCCESS };
}
export function restoreProjectLogCategoryError( error: any ): RestoreProjectLogCategoryErrorAction {
    return { type: RESTORE_PROJECT_LOG_CATEGORY_ERROR, error };
}

//redux observable epic
export function restoreProjectLogCategoryEpic(
    action$: Observable< RestoreProjectLogCategoryAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_PROJECT_LOG_CATEGORY ),
        exhaustMap( action => ajax(
            APP_CONFIG.getRequestObject( pathWithId( action.projectLogCategoryId ), RouteMethods.patch )
        ).pipe(
            mergeMap( _ => of(
                restoreProjectLogCategorySuccess(),
                eventNotificationSuccess(),
                fetchProjectLogCategories( ProjectLogModule.identifier )
            ) ),
            catchError( error => of(
                restoreProjectLogCategoryError( error ),
                eventNotificationError( error, store$, ProjectLogModule.identifier, "restoreProjectLogCategory" )
            ) )
        ))
    );
}